import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Grid,
    InputAdornment,
    MenuItem,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EuroIcon from '@mui/icons-material/Euro';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { postIngresoApartado } from '../../services/apis';

const initialForm = {
    nombre: '',
    cantidad: '',
    id_usuario: '',
    ingreso_general: 0,
    id_concepto_contabilidad: '',
};

const getUsuarioId = (usuario) => usuario.id_usuario ?? usuario.id ?? usuario.value;
const getUsuarioNombre = (usuario) => usuario.nombre_usuario ?? usuario.nombre ?? usuario.label ?? '';

export const AnadirTipoIngresoDialog = ({
    open,
    onClose,
    currenApartado,
    cargarDatos,
    usuarios = [],
    usuarioActualId = '',
    selectorUsuarioBloqueado = false,
    usuarioPreseleccionadoId = '',
    ingresoGeneralPreseleccionado = false,
    conceptos = []
}) => {
    const [form, setForm] = useState(initialForm);
    const [guardando, setGuardando] = useState(false);
    const [error, setError] = useState('');
    const nombreUsuarioActual = useMemo(() => localStorage.getItem('DesRepresentante') || '', []);

    const getUsuarioPorDefecto = () => {
        const usuarioPreseleccionado = usuarios.find((usuario) => String(getUsuarioId(usuario)) === String(usuarioPreseleccionadoId));
        const usuarioActual = usuarios.find((usuario) => String(getUsuarioId(usuario)) === String(usuarioActualId))
            || usuarios.find((usuario) => (
                getUsuarioNombre(usuario).toLowerCase() === nombreUsuarioActual.toLowerCase()
            ));

        return usuarioPreseleccionado || usuarioActual || usuarios[0] || null;
    };

    useEffect(() => {
        if (!open) {
            return;
        }

        if (ingresoGeneralPreseleccionado) {
            setForm((prev) => ({
                ...prev,
                id_usuario: '',
                ingreso_general: 1,
            }));
            return;
        }

        if (usuarios.length === 0) {
            return;
        }

        const usuarioPorDefecto = getUsuarioPorDefecto();

        setForm((prev) => ({
            ...prev,
            id_usuario: usuarioPorDefecto ? getUsuarioId(usuarioPorDefecto) : '',
            ingreso_general: 0,
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ingresoGeneralPreseleccionado, nombreUsuarioActual, open, usuarioActualId, usuarioPreseleccionadoId, usuarios]);

    const handleChange = (event) => {
        const { name, value } = event.target;
        setForm((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleGeneralChange = (event) => {
        const checked = event.target.checked;
        const usuarioPorDefecto = checked ? null : getUsuarioPorDefecto();
        setForm((prev) => ({
            ...prev,
            ingreso_general: checked ? 1 : 0,
            id_usuario: checked ? '' : (usuarioPorDefecto ? getUsuarioId(usuarioPorDefecto) : prev.id_usuario),
        }));
    };

    const resetForm = () => {
        setForm(initialForm);
        setError('');
    };

    const handleClose = () => {
        if (guardando) {
            return;
        }

        resetForm();
        onClose();
    };

    const validar = () => {
        if (!form.nombre.trim()) {
            return "El nom de l'ingrés és obligatori.";
        }

        if (Number(form.ingreso_general) !== 1 && !form.id_usuario) {
            return "Selecciona l'usuari a qui s'assigna l'ingrés.";
        }

        if (form.cantidad === '' || Number(form.cantidad) <= 0) {
            return 'Introdueix una quantitat vàlida.';
        }

        return '';
    };

    const handleAdd = async () => {
        const validationError = validar();
        if (validationError) {
            setError(validationError);
            return;
        }

        setGuardando(true);
        setError('');

        try {
            const response = await postIngresoApartado({
                nombre: form.nombre.trim(),
                cantidad: form.cantidad,
                id_usuario: Number(form.ingreso_general) === 1 ? null : form.id_usuario,
                ingreso_general: Number(form.ingreso_general) === 1 ? 1 : 0,
                id_apartado: currenApartado,
                id_concepto_contabilidad: form.id_concepto_contabilidad || null,
            });

            if (response?.success === true) {
                await cargarDatos();
                resetForm();
                onClose();
                return;
            }

            setError('Ha sorgit un error.');
        } catch (err) {
            console.error("Error en afegir l'ingrés:", err);
            setError('Ha sorgit un error.');
        } finally {
            setGuardando(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle className="income-dialog-title">
                <Box className="income-dialog-icon">
                    <TrendingUpIcon />
                </Box>
                <Box className="income-dialog-heading">
                    <Typography className="accounting-eyebrow">Ingrés</Typography>
                    <Typography variant="h5">
                        Afegir ingrés
                    </Typography>
                </Box>
            </DialogTitle>

            <DialogContent dividers className="income-dialog-content">
                {error && (
                    <Alert severity="error" className="income-dialog-alert">
                        {error}
                    </Alert>
                )}

                <Grid container spacing={2}>
                    <Grid size={{ xs: 12 }}>
                        <TextField
                            fullWidth
                            required
                            label="Nom de l'ingrés"
                            name="nombre"
                            value={form.nombre}
                            onChange={handleChange}
                            inputProps={{ maxLength: 80 }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={Number(form.ingreso_general) === 1}
                                    onChange={handleGeneralChange}
                                    disabled={Boolean(usuarioPreseleccionadoId) || ingresoGeneralPreseleccionado}
                                />
                            }
                            label="Ingrés general"
                        />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <TextField
                            fullWidth
                            select
                            required
                            label="Usuari"
                            name="id_usuario"
                            value={form.id_usuario}
                            onChange={handleChange}
                            disabled={selectorUsuarioBloqueado || Number(form.ingreso_general) === 1}
                        >
                            {usuarios.map((usuario) => (
                                <MenuItem key={getUsuarioId(usuario)} value={getUsuarioId(usuario)}>
                                    {getUsuarioNombre(usuario)}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <TextField
                            fullWidth
                            select
                            label="Concepte predefinit"
                            name="id_concepto_contabilidad"
                            value={form.id_concepto_contabilidad || ''}
                            onChange={handleChange}
                        >
                            <MenuItem value="">No definit</MenuItem>
                            {conceptos.map((concepto) => (
                                <MenuItem key={concepto.id} value={concepto.id}>
                                    {concepto.nombre}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            required
                            label="Quantitat"
                            name="cantidad"
                            type="number"
                            value={form.cantidad}
                            onChange={handleChange}
                            inputProps={{ min: '0', step: '0.01' }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <EuroIcon className="income-input-icon" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions className="income-dialog-actions">
                <Button onClick={handleClose} disabled={guardando}>
                    Cancel·lar
                </Button>
                <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd} disabled={guardando}>
                    {guardando ? 'Guardant...' : 'Guardar ingrés'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
